import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Car, CalendarDays, Clock3, Globe, LogOut, MapPin, Shield, UserRound, CheckCircle2, PlayCircle, Navigation, Phone, BellRing, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { fetchDriverRecord, fetchDriverTrips, formatDate, formatTime, updateDriverTripStatus } from "@/lib/blackrideOperations";
import { BOOKING_STATUS_LABELS, STATUS_TONE_MAP } from "@/lib/blackride-constants";

function StatusBadge({ value }) {
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_TONE_MAP[value] || "border-white/10 bg-white/5 text-gray-300"}`}>
      {BOOKING_STATUS_LABELS[value] || value || "—"}
    </span>
  );
}

function buildNavigationUrl(destination) {
  return `https://www.google.com/maps/dir/?api=1&travelmode=driving&destination=${encodeURIComponent(destination || "")}`;
}

export default function ChauffeurDashboard() {
  const { user, profile, signOut } = useAuth();
  const [driverRecord, setDriverRecord] = useState(null);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingTripId, setUpdatingTripId] = useState("");
  const [notificationsEnabled, setNotificationsEnabled] = useState(typeof Notification !== "undefined" ? Notification.permission === "granted" : false);
  const hasPrimedNotifications = useRef(false);
  const knownAssignedTrips = useRef(new Set());

  const loadDashboard = useCallback(async ({ silent = false } = {}) => {
    if (!user?.id) return;
    if (!silent) {
      setLoading(true);
    }
    setError("");
    try {
      const driver = await fetchDriverRecord(user.id);
      setDriverRecord(driver);
      if (!driver) {
        setTrips([]);
        setError("This auth user is not linked to a driver record yet. Add the auth_user_id inside the Driver management tab.");
      } else {
        const data = await fetchDriverTrips(driver.id);
        setTrips(data);

        const assignedTrips = data.filter((trip) => ["confirmed", "assigned"].includes(trip.status));
        const incomingAssigned = new Set(assignedTrips.map((trip) => trip.id));

        if (hasPrimedNotifications.current && typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
          assignedTrips
            .filter((trip) => !knownAssignedTrips.current.has(trip.id))
            .forEach((trip) => {
              new Notification("BlackRide trip assigned", {
                body: `${trip.customer_name || trip.booking_reference} • ${formatDate(trip.date)} at ${formatTime(trip.time)}`,
              });
            });
        }

        knownAssignedTrips.current = incomingAssigned;
        hasPrimedNotifications.current = true;
      }
    } catch (loadError) {
      setError(loadError.message || "Unable to load the driver dashboard.");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [user?.id]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadDashboard({ silent: true });
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, [loadDashboard]);

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      Notification.requestPermission()
        .then((permission) => setNotificationsEnabled(permission === "granted"))
        .catch(() => setNotificationsEnabled(false));
    } else {
      setNotificationsEnabled(Notification.permission === "granted");
    }
  }, []);

  const upcomingTrips = useMemo(() => trips.filter((trip) => ["assigned", "confirmed"].includes(trip.status)), [trips]);
  const ongoingTrips = useMemo(() => trips.filter((trip) => trip.status === "ongoing"), [trips]);
  const completedTrips = useMemo(() => trips.filter((trip) => trip.status === "completed"), [trips]);

  const updateTrip = async (trip, nextStatus) => {
    setUpdatingTripId(trip.id);
    try {
      await updateDriverTripStatus(trip, nextStatus);
      await loadDashboard();
    } catch (updateError) {
      setError(updateError.message || "Unable to update this trip right now.");
    } finally {
      setUpdatingTripId("");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#0a0a0a]/96 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 md:px-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo-white.png" alt="BlackRide" className="h-8 md:h-10 w-auto object-contain" />
            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-[#C7A239]">Driver portal</p>
              <p className="text-sm text-gray-400">Assigned rides, pickup navigation, and live trip status controls</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-full border border-white/10 px-4 py-2 text-sm text-gray-300">{profile?.full_name || user?.email}</div>
            <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${notificationsEnabled ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-300" : "border-white/10 text-gray-400"}`}>
              <BellRing className="h-4 w-4" />
              {notificationsEnabled ? "Assignment alerts on" : "Assignment alerts limited"}
            </div>
            <button onClick={() => loadDashboard()} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-white"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
            <button onClick={signOut} className="inline-flex items-center gap-2 rounded-full bg-[#C7A239] px-5 py-2.5 text-sm font-semibold text-black"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 md:px-8 md:py-8">
        {error && <div className="mb-5 rounded-[28px] border border-red-500/20 bg-red-500/10 px-5 py-4 text-sm text-red-300">{error}</div>}

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-[28px] border border-white/10 bg-[#111111] p-5">
            <div className="flex items-center gap-2 text-sm text-gray-400"><CalendarDays className="h-4 w-4 text-[#C7A239]" /> Upcoming</div>
            <div className="mt-4 text-3xl font-semibold">{upcomingTrips.length}</div>
            <p className="mt-2 text-xs text-gray-500">Trips assigned and ready for departure</p>
          </div>
          <div className="rounded-[28px] border border-white/10 bg-[#111111] p-5">
            <div className="flex items-center gap-2 text-sm text-gray-400"><PlayCircle className="h-4 w-4 text-[#C7A239]" /> Ongoing</div>
            <div className="mt-4 text-3xl font-semibold">{ongoingTrips.length}</div>
            <p className="mt-2 text-xs text-gray-500">Trips currently in service</p>
          </div>
          <div className="rounded-[28px] border border-white/10 bg-[#111111] p-5">
            <div className="flex items-center gap-2 text-sm text-gray-400"><CheckCircle2 className="h-4 w-4 text-[#C7A239]" /> Completed</div>
            <div className="mt-4 text-3xl font-semibold">{completedTrips.length}</div>
            <p className="mt-2 text-xs text-gray-500">Trips closed by this chauffeur</p>
          </div>
        </div>

        <div className="mt-6 grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          <section className="rounded-[28px] border border-white/10 bg-[#111111] p-5 md:p-6">
            <h2 className="font-[Playfair_Display] text-3xl text-white">Chauffeur profile</h2>
            <div className="mt-5 space-y-4 text-sm text-gray-300">
              <div className="rounded-3xl border border-white/8 bg-[#0b0b0b] p-4">
                <div className="flex items-center gap-3 text-gray-400"><UserRound className="h-4 w-4 text-[#C7A239]" /> Name</div>
                <p className="mt-2 text-white">{driverRecord?.name || profile?.full_name || "—"}</p>
              </div>
              <div className="rounded-3xl border border-white/8 bg-[#0b0b0b] p-4">
                <div className="flex items-center gap-3 text-gray-400"><Shield className="h-4 w-4 text-[#C7A239]" /> License</div>
                <p className="mt-2 text-white">{driverRecord?.license_number || "Not set"}</p>
              </div>
              <div className="rounded-3xl border border-white/8 bg-[#0b0b0b] p-4">
                <div className="flex items-center gap-3 text-gray-400"><Globe className="h-4 w-4 text-[#C7A239]" /> Languages</div>
                <p className="mt-2 text-white">{Array.isArray(driverRecord?.languages) && driverRecord.languages.length ? driverRecord.languages.join(", ") : "Not set"}</p>
              </div>
              <div className="rounded-3xl border border-white/8 bg-[#0b0b0b] p-4">
                <div className="flex items-center gap-3 text-gray-400"><Car className="h-4 w-4 text-[#C7A239]" /> Availability</div>
                <div className="mt-2"><StatusBadge value={driverRecord?.availability || "available"} /></div>
              </div>
            </div>
          </section>

          <section className="rounded-[28px] border border-white/10 bg-[#111111] p-5 md:p-6">
            <h2 className="font-[Playfair_Display] text-3xl text-white">Assigned trips</h2>
            <p className="mt-2 text-sm text-gray-400">Optimized for quick mobile updates while you are on the road.</p>
            <div className="mt-5 space-y-4">
              {loading ? (
                <p className="text-sm text-gray-500">Loading trips...</p>
              ) : trips.length === 0 ? (
                <p className="text-sm text-gray-500">No trips assigned yet.</p>
              ) : (
                trips.map((trip) => (
                  <article key={trip.id} className="rounded-[28px] border border-white/10 bg-[#0b0b0b] p-5 shadow-[0_18px_40px_rgba(0,0,0,0.18)]">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-[Playfair_Display] text-2xl text-white">{trip.customer_name || trip.booking_reference}</p>
                        <p className="mt-1 text-sm text-gray-500">{trip.booking_reference} • {trip.service_type === "hourly" ? "Hourly Booking" : "Drop off"}</p>
                      </div>
                      <StatusBadge value={trip.status} />
                    </div>

                    <div className="mt-5 grid gap-3 text-sm text-gray-300 md:grid-cols-2">
                      <div className="rounded-3xl border border-white/8 bg-white/[0.03] p-4">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-gray-500"><MapPin className="h-3.5 w-3.5 text-[#C7A239]" /> Pickup</div>
                        <p className="mt-2 leading-6">{trip.pickup_location}</p>
                      </div>
                      <div className="rounded-3xl border border-white/8 bg-white/[0.03] p-4">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-gray-500"><MapPin className="h-3.5 w-3.5 text-[#C7A239]" /> Drop-off</div>
                        <p className="mt-2 leading-6">{trip.drop_location || "Hourly service"}</p>
                      </div>
                      <div className="rounded-3xl border border-white/8 bg-white/[0.03] p-4">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-gray-500"><CalendarDays className="h-3.5 w-3.5 text-[#C7A239]" /> Schedule</div>
                        <p className="mt-2 leading-6">{formatDate(trip.date)} at {formatTime(trip.time)}</p>
                      </div>
                      <div className="rounded-3xl border border-white/8 bg-white/[0.03] p-4">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-gray-500"><Clock3 className="h-3.5 w-3.5 text-[#C7A239]" /> Notes</div>
                        <p className="mt-2 leading-6">{trip.notes || "No special notes"}</p>
                      </div>
                    </div>

                    <div className="mt-5 flex flex-wrap gap-3">
                      <a href={buildNavigationUrl(trip.pickup_location)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-5 py-3 text-sm font-semibold text-white">
                        <Navigation className="h-4 w-4 text-[#C7A239]" /> Navigate to pickup
                      </a>
                      {trip.customer_phone && (
                        <a href={`tel:${trip.customer_phone}`} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-5 py-3 text-sm font-semibold text-white">
                          <Phone className="h-4 w-4 text-[#C7A239]" /> Call guest
                        </a>
                      )}
                      {trip.status === "assigned" && (
                        <button onClick={() => updateTrip(trip, "ongoing")} disabled={updatingTripId === trip.id} className="inline-flex items-center gap-2 rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black disabled:opacity-60">
                          <PlayCircle className="h-4 w-4" /> {updatingTripId === trip.id ? "Updating..." : "Start trip"}
                        </button>
                      )}
                      {trip.status === "ongoing" && (
                        <button onClick={() => updateTrip(trip, "completed")} disabled={updatingTripId === trip.id} className="inline-flex items-center gap-2 rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black disabled:opacity-60">
                          <CheckCircle2 className="h-4 w-4" /> {updatingTripId === trip.id ? "Updating..." : "Complete trip"}
                        </button>
                      )}
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
